import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { useToggleSubscription } from '@sorare/core/src/hooks/subscriptions/useToggleSubscription';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useRemoveFromWatchlist } from 'hooks/watchlist/useRemoveFromWatchlist';

import type { RemoveFromWatchlistButton_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: RemoveFromWatchlistButton_anyPlayer;
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  onClose?: () => void;
};

export const RemoveFromWatchlistButton = ({
  anyPlayer,
  watchlistId,
  favoritesWatchlist,
  onClose,
}: Props) => {
  const { removeFromWatchlist } = useRemoveFromWatchlist();

  const { refresh } = useInstantSearch() || {};
  const toggleSubscription = useToggleSubscription(
    anyPlayer.currentUserSubscription,
    anyPlayer,
    undefined,
    [namedOperations.Query.FavoritesWatchlistPageQuery]
  );

  const onRemove = async () => {
    if (watchlistId) {
      await removeFromWatchlist.mutate(watchlistId, [
        anyPlayer.commonPlayer?.id || '',
      ]);
      return;
    }
    if (favoritesWatchlist) {
      toggleSubscription();
    }
  };

  return (
    <LoadingButton
      loading={removeFromWatchlist.loading}
      size="medium"
      fullWidth
      color="red"
      blur
      onClick={async () => {
        await onRemove();
        refresh?.();
        onClose?.();
      }}
      startIcon={<FontAwesomeIcon icon={faTrash} />}
    >
      <FormattedMessage {...glossary.remove} />
    </LoadingButton>
  );
};

RemoveFromWatchlistButton.fragments = {
  anyPlayer: gql`
    fragment RemoveFromWatchlistButton_anyPlayer on AnyPlayerInterface {
      slug
      currentUserSubscription {
        slug
      }
      commonPlayer(rarity: common) {
        id
      }
    }
  ` as TypedDocumentNode<RemoveFromWatchlistButton_anyPlayer>,
};
